/**
 * WorkspacesView — agency workspace management.
 *
 * S-061 / FEAT-006 (Phase 4). Replaces the AdminStubs.WorkspacesView
 * placeholder. Admin-only screen reachable from Settings → Workspaces.
 *
 * What it does:
 *   - Lists every agencyclient visible to the caller via GET /agencyclients
 *     (RLS already scopes the response to the caller's tenant).
 *   - Lets an admin create a new workspace (POST /agencyclients) with a
 *     name + auto-derived slug. Slug is editable before submit; slug
 *     collisions surface inline ("slug_taken").
 *   - Opens a side drawer to edit an existing workspace (name, slug,
 *     byoapi_enabled toggle) via PATCH /agencyclients/:id. Same 409
 *     handling as create.
 *   - Archives via DELETE /agencyclients/:id. The API performs a hard
 *     delete (FEAT-002 convention — soft-delete lives on `pieces`, not
 *     here). A confirm modal warns the user that the row is destroyed
 *     and prefers "archive" wording for the button copy since the spec
 *     calls this an archive action; the underlying API behavior is
 *     unchanged.
 *
 * RBAC:
 *   - The route is reachable only from the SettingsNav rail which is
 *     gated on Clerk `org:admin` (see LeftRail.SettingsNav).
 *   - The API enforces requireAdmin server-side on POST/PATCH/DELETE.
 *   - This view doesn't render its own RBAC guard — a non-admin who
 *     somehow lands here will see the GET list (which they're allowed
 *     to read) but every mutation will 403 with the friendly message
 *     from ApiError.
 *
 * Edge cases handled:
 *   - Empty list — render an empty state inviting the user to create
 *     their first workspace.
 *   - Slug collision — inline error on the slug field (server returns
 *     409 with body `{"error":"slug_taken"}`).
 *   - Long names/slugs — table cells truncate with ellipsis; the drawer
 *     shows the full value.
 *   - "Show archived" — the API hard-deletes, so there are no archived
 *     rows to filter. The toggle is still rendered for forward-compat
 *     with a future soft-delete migration, but is no-op today; we hide
 *     it to avoid misleading the user.
 */

import { useEffect, useMemo, useState } from "react";
// Consolidation (option 3): shared admin modal/drawer shell. Replaces the
// local ModalShell/DrawerShell that used to live at the bottom of this file.
import { ModalShell, DrawerShell } from "@/components/SettingsModalShell";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiMutation, useApiQuery } from "@/lib/api";
import { UserPicker } from "@/components/UserPicker";
import { SectionHeader } from "@/components/SectionHeader";
import { REVIEW_STAGES } from "@shared/fiction-phases";
import {
  LLM_PROVIDERS,
  hasActiveKeyFor,
  type ApiKeyLike,
} from "@/lib/llm-providers";

const ALL_REVIEW_STAGE_VALUES = REVIEW_STAGES.map((s) => s.value);

type MembershipRole = "admin" | "editor" | "reviewer";

const ROLE_LABELS: Record<MembershipRole, string> = {
  admin: "Admin",
  editor: "Editor",
  reviewer: "Reviewer",
};

interface Agencyclient {
  id: number;
  tenantId: number;
  name: string;
  slug: string;
  byoapiEnabled?: boolean;
  createdAt?: string;
  // FEAT-009 — read-only on this view. Edit modal does not surface it;
  // operator owns the flip via bin/set-run-mode.ts. Optional in the type
  // to remain forward-compatible with API responses minted before 0015.
  runMode?: "auto" | "manual";
  run_mode?: "auto" | "manual";
  // Drizzle returns columns as camelCase; accept either shape to be
  // safe against future API tweaks.
  byoapi_enabled?: boolean;
  created_at?: string;
  // FEAT-025 — workspace-level default review gates.
  reviewStages?: string[];
  review_stages?: string[];
  // FEAT-013 — workspace default LLM provider. null = priority fallback.
  preferredLlmProvider?: string | null;
  preferred_llm_provider?: string | null;
  // FEAT-049 — workspace-level default Voice Profile / Brand Identity.
  // null = no default; publications without their own pick fall through
  // to the Writer's hardcoded generic voice.
  defaultVoiceProfileId?: number | null;
  default_voice_profile_id?: number | null;
  defaultBrandIdentityId?: number | null;
  default_brand_identity_id?: number | null;
}

interface UpdateBody {
  name?: string;
  slug?: string;
  byoapi_enabled?: boolean;
  review_stages?: string[];
  // FEAT-013 — null = clear (priority fallback); slug = explicit provider.
  preferred_llm_provider?: string | null;
  // FEAT-049 — null = clear (publications must pick their own / fall through).
  default_voice_profile_id?: number | null;
  default_brand_identity_id?: number | null;
}

// FEAT-049 — matches PublicationsView.tsx's VoiceProfile/BrandIdentity
// response shape (id + name are all this picker needs).
interface VoiceProfileOption {
  id: number;
  name: string;
}

interface BrandIdentityOption {
  id: number;
  name: string;
}

interface VoiceProfileOptionsResponse {
  data: VoiceProfileOption[];
  meta: { total: number; limit: number; offset: number };
}

interface BrandIdentityOptionsResponse {
  data: BrandIdentityOption[];
  meta: { total: number; limit: number; offset: number };
}

// Mirrors the slug regex in apps/api/src/lib/validation.ts so we
// reject invalid slugs client-side before round-tripping to a 422.
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function isValidSlug(s: string): boolean {
  if (s.length < 1 || s.length > 60) return false;
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(s);
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function byoapiOn(ac: Agencyclient): boolean {
  return ac.byoapiEnabled ?? ac.byoapi_enabled ?? false;
}

// FEAT-013 — workspace default provider preference (null = priority fallback).
function preferredProviderOf(ac: Agencyclient): string | null {
  return ac.preferredLlmProvider ?? ac.preferred_llm_provider ?? null;
}

// FEAT-049 — workspace-level default Voice Profile / Brand Identity.
function defaultVoiceProfileIdOf(ac: Agencyclient): number | null {
  return ac.defaultVoiceProfileId ?? ac.default_voice_profile_id ?? null;
}

function defaultBrandIdentityIdOf(ac: Agencyclient): number | null {
  return ac.defaultBrandIdentityId ?? ac.default_brand_identity_id ?? null;
}

// FEAT-025 — workspace default review stages (all 7 when unset).
function reviewStagesOf(ac: Agencyclient): string[] {
  const raw = ac.reviewStages ?? ac.review_stages;
  return Array.isArray(raw) ? raw : ALL_REVIEW_STAGE_VALUES;
}

function sameStages(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function createdAtOf(ac: Agencyclient): string | undefined {
  return ac.createdAt ?? ac.created_at;
}

export function WorkspacesView() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useApiQuery<Agencyclient[]>(
    ["agencyclients"],
    "/agencyclients"
  );

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Agencyclient | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<Agencyclient | null>(
    null
  );

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["agencyclients"] });
  }

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <SectionHeader
          eyebrow="Settings · Workspaces"
          title="Workspaces"
          description="One workspace per client engagement. Add as many as you need."
          action={
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowCreate(true)}
            >
              + New workspace
            </button>
          }
        />

        {isLoading && (
          <div className="state-loading">Loading workspaces…</div>
        )}

        {error && !isLoading && (
          <div className="state-error">
            {error instanceof ApiError && error.status === 403
              ? "You don't have permission to view workspaces."
              : (error.message ?? "Could not load workspaces.")}
          </div>
        )}

        {!isLoading && !error && (data?.length ?? 0) === 0 && (
          <EmptyState onCreate={() => setShowCreate(true)} />
        )}

        {!isLoading && !error && (data?.length ?? 0) > 0 && (
          <WorkspacesTable
            rows={data!}
            onEdit={setEditing}
            onArchive={setConfirmArchive}
          />
        )}
      </div>

      {showCreate && (
        <CreateWorkspaceModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            invalidate();
            setShowCreate(false);
          }}
        />
      )}

      {editing && (
        <EditWorkspaceDrawer
          workspace={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
          }}
        />
      )}

      {confirmArchive && (
        <ConfirmArchiveModal
          workspace={confirmArchive}
          onCancel={() => setConfirmArchive(null)}
          onArchived={() => {
            invalidate();
            setConfirmArchive(null);
          }}
        />
      )}
    </main>
  );
}

// ---------- List + empty state ----------

interface WorkspacesTableProps {
  rows: Agencyclient[];
  onEdit: (ac: Agencyclient) => void;
  onArchive: (ac: Agencyclient) => void;
}

function WorkspacesTable({ rows, onEdit, onArchive }: WorkspacesTableProps) {
  return (
    <table className="workspaces-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Slug</th>
          <th>BYOAPI</th>
          <th>Created</th>
          <th className="ws-actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((ac) => (
          <tr
            key={ac.id}
            className="workspaces-row"
            onClick={() => onEdit(ac)}
          >
            <td className="ws-name-cell">
              <span className="ws-name">{ac.name}</span>
              {(ac.runMode ?? ac.run_mode) === "manual" && (
                <span
                  className="pill ws-mode-pill"
                  title="Operator-triggered — runs from the CLI, not the SPA"
                >
                  Operator
                </span>
              )}
            </td>
            <td>
              <code className="mono ws-slug">{ac.slug}</code>
            </td>
            <td>
              <span
                className={`pill ${byoapiOn(ac) ? "pill-ok" : "pill-muted"}`}
              >
                {byoapiOn(ac) ? "On" : "Off"}
              </span>
            </td>
            <td className="ws-date">{fmtDate(createdAtOf(ac))}</td>
            <td
              className="ws-actions"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="btn-ghost"
                onClick={() => onEdit(ac)}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn-ghost btn-danger"
                onClick={() => onArchive(ac)}
              >
                Archive
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="settings-empty">
      <h2>No workspaces yet</h2>
      <p>
        Your first workspace was created during onboarding. Add another to
        keep client engagements separate.
      </p>
      <button type="button" className="btn-primary" onClick={onCreate}>
        + New workspace
      </button>
    </div>
  );
}

// ---------- Create modal ----------

interface CreateProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateWorkspaceModal({ onClose, onCreated }: CreateProps) {
  const api = useApi();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  // Track whether the user has manually touched the slug; otherwise we
  // auto-derive from name on every keystroke.
  const [slugDirty, setSlugDirty] = useState(false);

  // S-179 — optional "add team members" step. Selected existing tenant
  // users are added to the new workspace via POST /memberships right after
  // it's created.
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const [memberRole, setMemberRole] = useState<MembershipRole>("editor");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  // Auto-slugify until the user types in the slug field.
  useEffect(() => {
    if (!slugDirty) {
      setSlug(slugify(name));
    }
  }, [name, slugDirty]);

  function toggleMember(id: string) {
    setMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const slugError = slug.length > 0 && !isValidSlug(slug);
  const nameError = name.trim().length === 0 || name.trim().length > 120;
  const collisionError = err instanceof ApiError && err.status === 409;
  const validationError = err instanceof ApiError && err.status === 422;
  const canSubmit = !busy && !nameError && !slugError && slug.length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const created = await api<Agencyclient>("/agencyclients", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), slug }),
      });
      // Add selected existing users to the freshly created workspace. A
      // failure here shouldn't lose the created workspace — surface it but
      // still close, since the workspace itself succeeded.
      for (const clerkUserId of memberIds) {
        try {
          await api("/memberships", {
            method: "POST",
            body: JSON.stringify({
              clerk_user_id: clerkUserId,
              agencyclient_id: created.id,
              role: memberRole,
            }),
          });
        } catch {
          // Best-effort: keep going; the admin can add stragglers later
          // from the workspace's Team view.
        }
      }
      onCreated();
    } catch (e2) {
      setErr(
        e2 instanceof ApiError
          ? e2
          : new ApiError(0, String(e2), "Could not create workspace")
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell onClose={onClose} eyebrow="Workspaces" title="New workspace">
      <form className="settings-form" onSubmit={(e) => void submit(e)}>
        <label className="settings-field">
          <span className="settings-label">Name</span>
          <input
            className="settings-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            autoFocus
            placeholder="e.g. Acme Manufacturing"
          />
          {nameError && name.length > 0 && (
            <span className="settings-field-error">
              Name must be 1–120 characters.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">
            Slug <span className="settings-label-hint">URL-safe identifier</span>
          </span>
          <input
            className="settings-input mono"
            type="text"
            value={slug}
            maxLength={60}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugDirty(true);
            }}
            placeholder="acme-manufacturing"
          />
          {slugError && (
            <span className="settings-field-error">
              Kebab-case only: lowercase letters, digits, and hyphens.
            </span>
          )}
          {collisionError && (
            <span className="settings-field-error">
              That slug is already taken in your tenant.
            </span>
          )}
        </label>

        <div className="settings-field">
          <span className="settings-label">
            Team members{" "}
            <span className="settings-label-hint">optional</span>
          </span>
          <span
            className="settings-label-hint"
            style={{ marginBottom: 6, fontSize: 12 }}
          >
            Add existing teammates to this workspace now. You (the creator)
            are added as admin automatically.
          </span>
          <UserPicker
            selectedIds={memberIds}
            onToggle={toggleMember}
            emptyLabel="No other teammates yet — invite some from Team."
          />
          {memberIds.size > 0 && (
            <label className="settings-field" style={{ marginTop: 8 }}>
              <span className="settings-label">Role for added members</span>
              <select
                className="settings-select"
                value={memberRole}
                onChange={(e) =>
                  setMemberRole(e.target.value as MembershipRole)
                }
              >
                <option value="admin">{ROLE_LABELS.admin}</option>
                <option value="editor">{ROLE_LABELS.editor}</option>
                <option value="reviewer">{ROLE_LABELS.reviewer}</option>
              </select>
            </label>
          )}
        </div>

        {validationError && !collisionError && (
          <div className="settings-form-error">
            {err?.body || "The server rejected the request."}
          </div>
        )}
        {err && !collisionError && !validationError && err.status !== 409 && (
          <div className="settings-form-error">{err.message}</div>
        )}

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!canSubmit}>
            {busy ? "Creating…" : "Create workspace"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------- Edit drawer ----------

interface EditProps {
  workspace: Agencyclient;
  onClose: () => void;
  onSaved: () => void;
}

function EditWorkspaceDrawer({ workspace, onClose, onSaved }: EditProps) {
  const [name, setName] = useState(workspace.name);
  const [slug, setSlug] = useState(workspace.slug);
  const [byoapi, setByoapi] = useState(byoapiOn(workspace));
  const initialReviewStages = reviewStagesOf(workspace);
  const [reviewStages, setReviewStages] = useState<string[]>(initialReviewStages);

  // FEAT-013 — workspace default provider. "" = no default (priority fallback).
  const initialProvider = preferredProviderOf(workspace);
  const [provider, setProvider] = useState<string>(initialProvider ?? "");
  const { data: apiKeys } = useApiQuery<{ data: ApiKeyLike[] }>(
    ["api-keys"],
    "/api-keys"
  );
  // S-374 — keys stored ON this workspace, so the BYOAPI row can say what is
  // actually there rather than describing the setting in the abstract.
  const { data: wsKeys } = useApiQuery<{ data: ApiKeyLike[] }>(
    ["api-keys", workspace.id],
    `/api-keys?agencyclient_id=${workspace.id}`
  );
  const wsActiveKeys = (wsKeys?.data ?? []).filter((k) => k.status === "active");
  const providerHasNoKey =
    provider !== "" && !hasActiveKeyFor(apiKeys?.data, provider);

  // FEAT-049 — workspace default Voice Profile / Brand Identity. "" = no
  // default. Scoped to this workspace, same query pattern PublicationsView
  // uses for its own voice/brand pickers.
  const initialDefaultVoiceId = defaultVoiceProfileIdOf(workspace);
  const [defaultVoiceId, setDefaultVoiceId] = useState<string>(
    initialDefaultVoiceId != null ? String(initialDefaultVoiceId) : ""
  );
  const initialDefaultBrandId = defaultBrandIdentityIdOf(workspace);
  const [defaultBrandId, setDefaultBrandId] = useState<string>(
    initialDefaultBrandId != null ? String(initialDefaultBrandId) : ""
  );
  const { data: voiceProfileOptions } = useApiQuery<VoiceProfileOptionsResponse>(
    ["voice-profiles", workspace.id],
    `/voice-profiles?agencyclient_id=${workspace.id}`
  );
  const { data: brandIdentityOptions } = useApiQuery<BrandIdentityOptionsResponse>(
    ["brand-identities", workspace.id],
    `/brand-identities?agencyclient_id=${workspace.id}`
  );

  const mutation = useApiMutation<Agencyclient, UpdateBody>(
    `/agencyclients/${workspace.id}`,
    "PATCH",
    {
      onSuccess: () => onSaved(),
    }
  );

  const reviewDirty = !sameStages(reviewStages, initialReviewStages);
  const providerDirty = provider !== (initialProvider ?? "");
  const defaultVoiceDirty =
    defaultVoiceId !== (initialDefaultVoiceId != null ? String(initialDefaultVoiceId) : "");
  const defaultBrandDirty =
    defaultBrandId !== (initialDefaultBrandId != null ? String(initialDefaultBrandId) : "");
  const dirty = useMemo(() => {
    return (
      name.trim() !== workspace.name ||
      slug !== workspace.slug ||
      byoapi !== byoapiOn(workspace) ||
      reviewDirty ||
      providerDirty ||
      defaultVoiceDirty ||
      defaultBrandDirty
    );
  }, [name, slug, byoapi, workspace, reviewDirty, providerDirty, defaultVoiceDirty, defaultBrandDirty]);

  const slugError = slug.length > 0 && !isValidSlug(slug);
  const nameError = name.trim().length === 0 || name.trim().length > 120;
  const collisionError =
    mutation.error instanceof ApiError && mutation.error.status === 409;
  const canSubmit =
    !mutation.isPending && dirty && !nameError && !slugError;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    // Only send the fields that changed — the API's PATCH schema
    // requires at least one field, but we'd rather not overwrite a
    // value the user didn't touch.
    const body: UpdateBody = {};
    if (name.trim() !== workspace.name) body.name = name.trim();
    if (slug !== workspace.slug) body.slug = slug;
    if (byoapi !== byoapiOn(workspace)) body.byoapi_enabled = byoapi;
    if (reviewDirty) body.review_stages = reviewStages;
    if (providerDirty) {
      body.preferred_llm_provider = provider === "" ? null : provider;
    }
    if (defaultVoiceDirty) {
      body.default_voice_profile_id = defaultVoiceId === "" ? null : Number(defaultVoiceId);
    }
    if (defaultBrandDirty) {
      body.default_brand_identity_id = defaultBrandId === "" ? null : Number(defaultBrandId);
    }
    mutation.mutate(body);
  }

  return (
    <DrawerShell onClose={onClose} eyebrow="Workspaces" title={`Edit workspace`}>
      <form className="settings-form" onSubmit={submit}>
        <label className="settings-field">
          <span className="settings-label">Name</span>
          <input
            className="settings-input"
            type="text"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          {nameError && (
            <span className="settings-field-error">
              Name must be 1–120 characters.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">Slug</span>
          <input
            className="settings-input mono"
            type="text"
            value={slug}
            maxLength={60}
            onChange={(e) => setSlug(e.target.value)}
          />
          {slugError && (
            <span className="settings-field-error">
              Kebab-case only: lowercase letters, digits, and hyphens.
            </span>
          )}
          {collisionError && (
            <span className="settings-field-error">
              That slug is already taken in your tenant.
            </span>
          )}
        </label>

        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={byoapi}
            onChange={(e) => setByoapi(e.target.checked)}
          />
          <span className="settings-toggle-text">
            <span className="settings-toggle-title">BYOAPI enabled</span>
            <span className="settings-toggle-sub">
              Marks this workspace as running on its own API keys.
            </span>
          </span>
        </label>
        {/* S-374 — the handoff has a footnote here saying that turning this
            on with no keys stops the pipeline. That is NOT true of this
            codebase: byoapi-resolver.ts never reads byoapi_enabled. It
            cascades workspace key → tenant key → platform key regardless of
            the toggle, so a workspace with no keys of its own keeps running
            on the tenant's. Writing the handoff's warning would put a false
            alarm on screen, so this says what is actually the case, and the
            inert toggle is filed as BUG-072. */}
        <div className="settings-guard-note" style={{ maxWidth: "60ch" }}>
          {wsActiveKeys.length > 0 ? (
            <>
              This workspace has {wsActiveKeys.length} active key
              {wsActiveKeys.length === 1 ? "" : "s"} of its own, and pipeline
              runs use {wsActiveKeys.length === 1 ? "it" : "them"} before the
              tenant's.
            </>
          ) : (
            <>
              No keys are stored on this workspace, so its pipeline runs use
              the tenant's keys. Add keys under Settings → API keys to bill
              this workspace's usage to its own account.
            </>
          )}
        </div>

        {/* FEAT-013 — workspace default LLM provider. Publications inherit
            this unless they set their own; "" means priority fallback. */}
        <label className="settings-field">
          <span className="settings-label">Default LLM provider</span>
          <select
            className="settings-select"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="">Default (automatic by key priority)</option>
            {LLM_PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          {providerHasNoKey && (
            <span className="settings-field-error">
              No active key for this provider. Add one in Settings → API Keys, or
              runs that fall back to this default will fail.
            </span>
          )}
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            Publications inherit this unless they choose their own provider.
          </span>
        </label>

        {/* FEAT-049 — workspace-level default Voice Profile / Brand Identity.
            Publications inherit these unless they pick their own. */}
        <label className="settings-field">
          <span className="settings-label">Default voice profile</span>
          <select
            className="settings-select"
            value={defaultVoiceId}
            onChange={(e) => setDefaultVoiceId(e.target.value)}
          >
            <option value="">(None — publications must pick their own)</option>
            {(voiceProfileOptions?.data ?? []).map((vp) => (
              <option key={vp.id} value={vp.id}>
                {vp.name}
              </option>
            ))}
          </select>
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            New publications inherit this unless they choose their own voice.
          </span>
        </label>

        <label className="settings-field">
          <span className="settings-label">Default brand identity</span>
          <select
            className="settings-select"
            value={defaultBrandId}
            onChange={(e) => setDefaultBrandId(e.target.value)}
          >
            <option value="">(None — publications must pick their own)</option>
            {(brandIdentityOptions?.data ?? []).map((bi) => (
              <option key={bi.id} value={bi.id}>
                {bi.name}
              </option>
            ))}
          </select>
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            New publications inherit this unless they choose their own brand.
          </span>
        </label>

        {/* FEAT-025 — workspace-level default review gates. Publications
            inherit these unless they set their own. */}
        <fieldset className="settings-field" style={{ border: "none", padding: 0, margin: 0 }}>
          <span className="settings-label">Default review gates</span>
          <div className="settings-check-group">
            {REVIEW_STAGES.map((stage) => (
              <label key={stage.value} className="settings-check-row">
                <input
                  type="checkbox"
                  checked={reviewStages.includes(stage.value)}
                  onChange={(e) =>
                    setReviewStages((prev) =>
                      e.target.checked
                        ? [...prev, stage.value]
                        : prev.filter((v) => v !== stage.value)
                    )
                  }
                />
                <span>{stage.label}</span>
              </label>
            ))}
          </div>
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            The pipeline pauses for human review after each checked stage.
            Publications inherit this unless they override it. Publishing always
            requires explicit approval.
          </span>
        </fieldset>

        {mutation.error && !collisionError && (
          <div className="settings-form-error">
            {mutation.error.message}
          </div>
        )}

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {mutation.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
}

// ---------- Archive confirm ----------

interface ConfirmArchiveProps {
  workspace: Agencyclient;
  onCancel: () => void;
  onArchived: () => void;
}

function ConfirmArchiveModal({
  workspace,
  onCancel,
  onArchived,
}: ConfirmArchiveProps) {
  const mutation = useApiMutation<void, void>(
    `/agencyclients/${workspace.id}`,
    "DELETE",
    {
      onSuccess: () => onArchived(),
    }
  );

  return (
    <ModalShell onClose={onCancel} eyebrow="Workspaces · danger" title="Archive workspace?">
      <p className="settings-confirm-copy">
        Remove <strong>{workspace.name}</strong> from your tenant? This
        deletes the workspace and all of its pieces, memberships, and
        publish configuration. There is no undo.
      </p>
      {mutation.error && (
        <div className="settings-form-error">
          {mutation.error.message}
        </div>
      )}
      <div className="settings-form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary btn-danger"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Archiving…" : "Archive workspace"}
        </button>
      </div>
    </ModalShell>
  );
}

// NOTE: the local ModalShell / DrawerShell that used to live here were removed
// in the option-3 consolidation — both now come from
// @/components/SettingsModalShell (imported at the top of this file). The shared
// shell renders the canonical anatomy (eyebrow + 24px serif title, bordered
// close, blurred overlay) and handles Escape/backdrop close.
